/* eslint-disable react-hooks/exhaustive-deps */
import { Button, ButtonPrimary } from '@/components/button'
import { ArrowBack } from '@/components/icons'
import { TextInput } from '@/components/input'
import { ROUTE_DASHBOARD, ROUTE_VERIFY_EMAIL } from '@/constants/Routes'
import { useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import { initiateEmail } from '@/services'
import { createForgotPasswordValidationSchema } from '@/utils/schema/ForgotPasswordValidationSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

type FormValues = {
  email: string
}

export const ForgotPassword = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const { token } = useAppSelector(authSelector)

  const [submitting, setSubmitting] = useState(false)

  const defaultValues: FormValues = {
    email: '',
  }

  const validationSchema = createForgotPasswordValidationSchema(t)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(validationSchema),
  })

  const onGoBack = () => navigate(-1)

  // No account-existence pre-check: telling an anonymous caller whether an address is registered
  // is an account-enumeration oracle, and the endpoint that did it has been removed.
  //
  // The outcome of the initiate call is discarded ON PURPOSE — success, rate limit, 5xx and
  // network failure all advance to the same verify-email step with the same generic message.
  // Surfacing any of them would re-open the oracle (a registered address is the only one that
  // can be rate-limited into a distinguishable error). Do not "fix" this by adding a toast.
  const onSubmit = handleSubmit(async (data) => {
    if (submitting) return
    setSubmitting(true)
    try {
      await initiateEmail(data.email)
    } catch {
      // intentionally ignored — see above
    } finally {
      setSubmitting(false)
    }
    navigate(ROUTE_VERIFY_EMAIL, {
      state: {
        email: data.email,
      },
    })
  })

  useEffect(() => {
    if (token) {
      navigate(ROUTE_DASHBOARD, { relative: 'path', replace: true })
    }
  }, [token])

  return (
    <div className="w-screen h-screen flex sm:justify-center sm:items-center">
      <div className="flex flex-col w-full px-4 sm:px-0 sm:w-[390px] gap-y-7">
        <div className="flex relative flex-col sm:flex-row sm:items-center mt-8 md:mt-0 gap-y-4">
          <Button onClick={onGoBack} className="hover:opacity-50 flex sm:absolute sm:-left-14 sm:top-1">
            <ArrowBack className="text-white" />
          </Button>
          <div className="flex flex-col gap-y-2">
            <h1 className="text-2xl xs:text-3xl font-bold text-white">{t('common.forgotPassword')}</h1>
            <p className="text-sm text-white text-opacity-70">{t('auth.forgotPassDesc')}</p>
          </div>
        </div>

        <div className="flex flex-col gap-y-4 mt-24 md:mt-0">
          <TextInput
            className="!flex-1"
            placeholder={t('common.emailPlaceHolder')}
            label={t('common.email')}
            error={errors?.email?.message}
            {...register('email')}
          />
          <div className="flex justify-end gap-x-4">
            <Button
              className="bg-white bg-opacity-10 px-5 py-[14px] text-sm font-semibold text-white hover:!bg-opacity-30"
              onClick={onGoBack}
            >
              {t('common.cancel')}
            </Button>
            <ButtonPrimary
              title={t('common.submit')}
              className="px-5 py-[14px] text-sm font-semibold disabled:opacity-60"
              onClick={onSubmit}
              disabled={submitting}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
